import {
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Button,
  TextField,
  RadioGroup,
  Radio,
  Collapse,
} from "@mui/material";
import { IFilterParams } from "ag-grid-community";
import { IFilterReactComp } from "ag-grid-react";
import React, { forwardRef, useImperativeHandle, useState, useEffect } from "react";

type NumberFilterProps = IFilterParams & {
  showBlank: boolean;
};

const NumberFilter = forwardRef<IFilterReactComp, NumberFilterProps>(
  ({ filterChangedCallback, valueGetter, showBlank }, ref) => {
    const [blankOrNumber, setBlankOrNumber] = useState<"blank" | "number">("number");
    const [filterActive, setFilterActive] = useState(false);
    const [trigger, setTrigger] = useState(false);
    const [type, setType] = useState<"greaterThanOrEqual" | "lessThanOrEqual" | "between">(
      "between"
    );
    const [from, setFrom] = useState<"" | number>("");
    const [to, setTo] = useState<"" | number>("");

    // expose AG Grid Filter Lifecycle callbacks
    useImperativeHandle(ref, () => {
      return {
        doesFilterPass(params) {
          const value = valueGetter(params.node);
          if (blankOrNumber === "number") {
            if (type === "greaterThanOrEqual") {
              return value !== "" && value >= from!;
            } else if (type === "lessThanOrEqual") {
              return value !== "" && value <= to!;
            } else {
              return value !== "" && value >= from! && value <= to!;
            }
          } else {
            return value === "";
          }
        },

        isFilterActive() {
          return filterActive;
        },

        getModel() {
          if (!filterActive) return null;
          const savedStatus = {
            blankOrNumber,
            type,
            from,
            to,
          };
          if (blankOrNumber === "number") {
            if (type === "between") {
              return {
                type: "between",
                filter: `${from}|${to}`,
                savedStatus,
              };
            } else {
              return {
                type,
                filter: from,
                savedStatus,
              };
            }
          }

          return {
            type: "isNetNew",
            savedStatus,
            filter: "(Blank)",
          };
        },

        setModel(model: any) {
          if (model?.savedStatus) {
            const { savedStatus } = model;
            setBlankOrNumber(savedStatus.blankOrNumber ?? "number");
            setType(savedStatus.type ?? "between");
            setFrom(savedStatus.from ?? "");
            setTo(savedStatus.to ?? "");
            setFilterActive(true);
            setTrigger(true);
          } else {
            setFilterActive(false);
            setFrom("");
            setTo("");
            setTrigger(true);
          }
        },
      };
    });

    const onClear = () => {
      setFilterActive(false);
      setFrom("");
      setTo("");
      setTrigger(true);
    };

    const onApply = () => {
      setFilterActive(true);
      setTrigger(true);
    };

    useEffect(() => {
      if (trigger) {
        filterChangedCallback();
        setTrigger(false);
      }
    }, [trigger, filterChangedCallback]);

    return (
      <Grid
        container={true}
        direction="column"
        className="ag-filter-body-wrapper ag-simple-filter-body-wrapper"
      >
        {showBlank === true ? (
          <RadioGroup
            value={blankOrNumber}
            //@ts-ignore
            onChange={(_e, v) => setBlankOrNumber(v)}
            style={{ flexDirection: "row" }}
          >
            <FormControlLabel
              value="number"
              control={<Radio color="primary" />}
              label="Numeric"
            />
            <FormControlLabel
              value="blank"
              control={<Radio color="primary" />}
              label="(Blank)"
            />
          </RadioGroup>
        ) : null}
        <Collapse in={blankOrNumber === "number"}>
          <Grid
            container={true}
            direction="column"
            spacing={1}
          >
            <Grid item={true}>
              <Select
                fullWidth={true}
                variant="outlined"
                margin="dense"
                size="small"
                value={type}
                //@ts-ignore
                onChange={(e) => setType(e.target.value)}
                MenuProps={{
                  disablePortal: true,
                  keepMounted: true,
                }}
              >
                <MenuItem value="between">Between</MenuItem>
                <MenuItem value="greaterThanOrEqual">Greater Than or Equal</MenuItem>
                <MenuItem value="lessThanOrEqual">Less Than or Equal</MenuItem>
              </Select>
            </Grid>
            <Grid item={true}>
              <TextField
                fullWidth={true}
                type="number"
                placeholder="Please input number"
                variant="standard"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value === "" ? "" : +e.target.value);
                }}
              />
            </Grid>
            {type === "between" ? (
              <Grid item={true}>
                <TextField
                  fullWidth={true}
                  type="number"
                  placeholder="To..."
                  variant="standard"
                  value={to}
                  onChange={(e) => {
                    setTo(e.target.value === "" ? "" : +e.target.value);
                  }}
                />
              </Grid>
            ) : null}
          </Grid>
        </Collapse>
        <Grid
          item={true}
          container={true}
          justifyContent="space-evenly"
        >
          <Grid item={true}>
            <Button
              variant="text"
              disabled={!filterActive}
              onClick={onClear}
              className="ag-standard-button ag-filter-apply-panel-button"
            >
              Reset filter
            </Button>
          </Grid>
          <Grid item={true}>
            <Button
              variant="text"
              className="ag-standard-button ag-filter-apply-panel-button"
              onClick={onApply}
              disabled={
                blankOrNumber === "number" && (from === "" || (type === "between" && to === ""))
              }
            >
              Apply filter
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  }
);

export default NumberFilter;
